/**
 * Yards to pixels. The world is the whole field, end zone to end zone, in device pixels (the
 * canvas renders at the screen's pixel ratio, so one sprite pixel can be one screen pixel on a
 * phone). The scale comes from the sprites: a Pokémon of median height stands about 2 yards tall
 * at a whole-number sprite scale, and the camera frames about 28 yards across.
 */
import { balance } from '@tdmons/sim';
import type { Spot } from '@tdmons/sim';
import spriteIndex from './spriteIndex.json';

/** Presentation only, not balance numbers. */
const TARGET_VISIBLE_YARDS = 28;
const TARGET_HEIGHT_YARDS = 2;
/** How tall the median Pokémon may stand, in yards, while the framing is fitted. */
const MIN_HEIGHT_YARDS = 1.75;
const MAX_HEIGHT_YARDS = 2.5;
const MAX_SPRITE_SCALE = 6;

export interface FieldScale {
  /** Whole-number sprite scale: 1 source pixel = this many device pixels. */
  spriteScale: number;
  pxPerYard: number;
}

/**
 * The sprite scale and pixels-per-yard for a field `widthPx` device pixels wide. Over every whole
 * sprite scale and every whole px/yard that keeps a `refHeight` sprite 1.75-2.5 yards tall, take
 * the one framing closest to 28 yards, then the one closest to 2 yards tall.
 */
export function pickScale(widthPx: number, refHeight: number): FieldScale {
  let best: (FieldScale & { off: number; tall: number }) | null = null;
  for (let s = 1; s <= MAX_SPRITE_SCALE; s++) {
    const lo = Math.ceil((refHeight * s) / MAX_HEIGHT_YARDS);
    const hi = Math.floor((refHeight * s) / MIN_HEIGHT_YARDS);
    for (let ppy = lo; ppy <= hi; ppy++) {
      const off = Math.abs(widthPx / ppy - TARGET_VISIBLE_YARDS);
      const tall = Math.abs((refHeight * s) / ppy - TARGET_HEIGHT_YARDS);
      if (
        best === null ||
        off < best.off - 1e-9 ||
        (Math.abs(off - best.off) <= 1e-9 && tall < best.tall)
      ) {
        best = { spriteScale: s, pxPerYard: ppy, off, tall };
      }
    }
  }
  const { spriteScale, pxPerYard } = best as FieldScale;
  return { spriteScale, pxPerYard };
}

export interface FieldGeometry extends FieldScale {
  /** World size in pixels. */
  width: number;
  height: number;
  endZoneYards: number;
  widthYards: number;
  /** Device pixels per CSS pixel: multiply every fixed CSS size (text, strokes, tap targets). */
  dpr: number;
  /** Field spot to world pixels, rounded to whole pixels (pixel art never sits between pixels). */
  toPx(spot: Spot): { x: number; y: number };
}

/** The field for a canvas `widthPx` device pixels wide at pixel ratio `dpr`. */
export function fieldGeometry(
  widthPx: number,
  dpr: number,
  refHeight: number = spriteIndex.refHeight,
): FieldGeometry {
  const { spriteScale, pxPerYard } = pickScale(widthPx, refHeight);
  const { endZoneYards, widthYards } = balance.field;
  return {
    spriteScale,
    pxPerYard,
    dpr,
    width: Math.round((100 + 2 * endZoneYards) * pxPerYard),
    height: Math.round(widthYards * pxPerYard),
    endZoneYards,
    widthYards,
    toPx: ([x, y]) => ({
      x: Math.round((x + endZoneYards) * pxPerYard),
      y: Math.round((y + widthYards / 2) * pxPerYard),
    }),
  };
}

/** Feet sit a quarter yard below the spot, so the ball carried at the spot sits on the body. */
export const FEET_BELOW_SPOT_YARDS = 0.25;
/** The HP bar under a Pokémon (slice 8c): this many yards wide, one CSS px of track around it. */
const HP_BAR_YARDS = 1.2;
const HP_BAR_EDGE_CSS_PX = 1;
/** Room the camera keeps above the ball for the carrier's body (a Pokémon is about 2 yards). */
const BODY_ABOVE_SPOT_YARDS = 2.5;
/** The lowest a body reaches below its spot: a sprite's feet, or half a placeholder shape. */
const BODY_BELOW_SPOT_YARDS = Math.max(FEET_BELOW_SPOT_YARDS, 0.6);
/** Half a Pokémon's width, kept beside each one when a formation is framed whole. */
const BODY_HALF_WIDTH_YARDS = 1;
/** And below the HP bar, in CSS px. */
const SAFE_GAP_CSS_PX = 4;

/** The HP bar's size in device px: its fill width and height, and the track's edge. */
export function hpBarSize(
  pxPerYard: number,
  dpr: number,
  barCssPx: number,
): { w: number; h: number; edge: number } {
  return {
    w: Math.max(8, Math.round(HP_BAR_YARDS * pxPerYard)),
    h: Math.max(2, Math.round(barCssPx * dpr)),
    edge: Math.max(1, Math.round(HP_BAR_EDGE_CSS_PX * dpr)),
  };
}

/**
 * What the camera keeps on screen around the ball, in device px: the carrier's body above it, and
 * below it his feet (or shape), the HP bar and its track, and a few CSS px more. In a formation
 * it keeps the same around every Pokémon, and `side` beside each: half a body or half the bar.
 */
export function cameraMargins(
  geo: Pick<FieldGeometry, 'pxPerYard' | 'dpr'>,
  barCssPx: number,
): { top: number; bottom: number; side: number } {
  const bar = hpBarSize(geo.pxPerYard, geo.dpr, barCssPx);
  return {
    side: Math.max(
      Math.round(BODY_HALF_WIDTH_YARDS * geo.pxPerYard),
      Math.ceil(bar.w / 2) + bar.edge,
    ),
    top: Math.round(BODY_ABOVE_SPOT_YARDS * geo.pxPerYard),
    bottom:
      Math.ceil(BODY_BELOW_SPOT_YARDS * geo.pxPerYard) +
      bar.edge +
      bar.h +
      2 * bar.edge +
      Math.round(SAFE_GAP_CSS_PX * geo.dpr),
  };
}

/**
 * Where a tag over a unit goes so it stays on screen (slice 8d), in world px. Centered on the unit
 * when it fits; near either edge of the view it flips inward (its outer edge pinned at the unit, so
 * it reads toward the middle of the field), and if it is still too wide it is clamped inside the
 * view, `pad` from the edge. `dx` is the tag's offset from the unit for that `originX`.
 */
export function labelAnchor(
  unitX: number,
  textW: number,
  viewLeft: number,
  viewRight: number,
  pad: number,
): { originX: 0 | 0.5 | 1; dx: number } {
  const lo = viewLeft + pad;
  const hi = viewRight - pad;
  const half = textW / 2;
  if (unitX - half >= lo && unitX + half <= hi) return { originX: 0.5, dx: 0 };
  if (unitX + half > hi) {
    // Near the right edge: the tag's right edge at the unit (or the view), reading leftward.
    const dx = Math.max(Math.min(0, hi - unitX), lo + textW - unitX);
    return { originX: 1, dx };
  }
  const dx = Math.min(Math.max(0, lo - unitX), hi - textW - unitX);
  return { originX: 0, dx };
}

/**
 * Whether a tag goes above its unit (slice 8d): the side's default (offense above, defense below,
 * floats above) unless that runs it off the top of the view or under the scorebug at the bottom
 * (`viewBottom` is the view's bottom less the scorebug's inset); then the other way. `top` and
 * `bottom` are the tag's anchor offsets from the unit, gap included.
 */
export function tagAbove(
  preferAbove: boolean,
  unitY: number,
  top: number,
  bottom: number,
  textH: number,
  viewTop: number,
  viewBottom: number,
): boolean {
  const fitsAbove = unitY + top - textH >= viewTop;
  const fitsBelow = unitY + bottom + textH <= viewBottom;
  return preferAbove ? fitsAbove || !fitsBelow : !fitsBelow && fitsAbove;
}
